import { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { Layer } from "../../types/layer";
import { Filter } from "./FilterModal";
import FolderBoundariesControl from "./FolderBoundariesControl";
import ProjectAreasControl from "./ProjectAreasControl";
import { LayerControlSideContext, type LayerControlSide } from "./layerControlSide";
import RealtimeControl from "./RealtimeControl";
import MeasureControl from "./MeasureControl";

interface ProjectListingLayerPanelProps {
  projectAreasVisible: boolean;
  onToggleProjectAreas: () => void;
  projectCount: number;
  /** The drawn projects' property boundaries, a checkbox under Project Areas. */
  propertiesVisible?: boolean;
  onTogglePropertiesVisible?: () => void;
  propertyCount?: number;
  propertyProjectCount?: number;
  propertiesTruncated?: boolean;
  propertiesLoading?: boolean;
  /** The folder layer (boundaries and extent boxes), toggled on its own. */
  foldersVisible?: boolean;
  onToggleFolders?: () => void;
  folderCount?: number;
  folderBoundaryCount?: number;
  pickingLocation: boolean;
  /** Absent = no "create a project here" button (the listing is browsing story maps). */
  onStartPickLocation?: () => void;
  onCancelPickLocation: () => void;
  hotspotsLayer: Layer | null;
  hotspotsFilters: Filter[];
  onHotspotsFiltersChange: (filters: Filter[]) => void;
  lightningLayer: Layer | null;
  lightningFilters: Filter[];
  onLightningFiltersChange: (filters: Filter[]) => void;
  hazardsLayer: Layer | null;
  hazardsFilters: Filter[];
  onHazardsFiltersChange: (filters: Filter[]) => void;
  ignitionPointsLayer: Layer | null;
  ignitionPointsFilters: Filter[];
  onIgnitionPointsFiltersChange: (filters: Filter[]) => void;
  burntAreasLayer: Layer | null;
  burntAreasFilters: Filter[];
  onBurntAreasFiltersChange: (filters: Filter[]) => void;
  simulatingDetection: boolean;
  onStartSimulateDetection: () => void;
  onCancelSimulateDetection: () => void;
  /**
   * "fixed" (default) pins the column to the viewport edge, as on the classic
   * full-width map. "absolute" keeps it inside a positioned map pane, so in
   * the split view it sits at the map's edge rather than over the table.
   */
  placement?: "fixed" | "absolute";
  /** Which edge of the map the pill column hugs. */
  side?: LayerControlSide;
  /** The distance-measuring tool (needs a DrawingProvider above the map). */
  measureLayer?: Layer | null;
}

/**
 * Layer sidebar for the project-listing map.
 *
 * Deliberately mirrors the project page's LayerControlPanel — same fixed
 * right-edge column of hover-expanding pills, same shared LayerControlItem
 * rows — but carries only the layers the listing map has. It is a separate
 * component because LayerControlPanel's props (and its ReportContext
 * dependency) are specific to a single opened project.
 */
const ProjectListingLayerPanel: React.FC<ProjectListingLayerPanelProps> = ({
  projectAreasVisible,
  onToggleProjectAreas,
  projectCount,
  propertiesVisible,
  onTogglePropertiesVisible,
  propertyCount,
  propertyProjectCount,
  propertiesTruncated,
  propertiesLoading,
  foldersVisible = true,
  onToggleFolders,
  folderCount = 0,
  folderBoundaryCount = 0,
  pickingLocation,
  onStartPickLocation,
  onCancelPickLocation,
  hotspotsLayer,
  hotspotsFilters,
  onHotspotsFiltersChange,
  lightningLayer,
  lightningFilters,
  onLightningFiltersChange,
  hazardsLayer,
  hazardsFilters,
  onHazardsFiltersChange,
  ignitionPointsLayer,
  ignitionPointsFilters,
  onIgnitionPointsFiltersChange,
  burntAreasLayer,
  burntAreasFilters,
  onBurntAreasFiltersChange,
  simulatingDetection,
  onStartSimulateDetection,
  onCancelSimulateDetection,
  placement = "fixed",
  side = "right",
  measureLayer = null,
}) => {
  const [expanded, setExpanded] = useState(false);
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handlePanelMouseEnter = () => {
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }
    setExpanded(true);
  };

  const handlePanelMouseLeave = () => {
    collapseTimeoutRef.current = setTimeout(() => {
      setExpanded(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  return (
    <LayerControlSideContext.Provider value={side}>
    <Box
      onMouseEnter={handlePanelMouseEnter}
      onMouseLeave={handlePanelMouseLeave}
      sx={{
        position: placement,
        ...(side === "left" ? { left: 10 } : { right: 10 }),
        top: "50%",
        transform: "translateY(-50%)",
        // Inside a pane the panel only has to clear the map's own controls;
        // 10000 would also sit over the coach tour's overlay.
        zIndex: placement === "fixed" ? 10000 : 30,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        overflowY: "auto",
        // Never a horizontal scrollbar: a pill's hover scale(1.05) counts as
        // scrollable overflow on the side it expands towards, and the thumb
        // styled below then shows up as a stray grey bar under the pills.
        overflowX: "hidden",
        maxHeight: "calc(var(--vh100) * 0.9)",
        // Room on both sides for that scale, so it is neither clipped nor scrolled.
        ...(side === "left" ? { paddingLeft: "8px", paddingRight: "12px" } : { paddingRight: "8px", paddingLeft: "12px" }),
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "rgba(255,255,255,0.3)",
          borderRadius: "3px",
        },
      }}
    >
      <ProjectAreasControl
        visible={projectAreasVisible}
        onToggleVisibility={onToggleProjectAreas}
        projectCount={projectCount}
        pickingLocation={pickingLocation}
        onStartPickLocation={onStartPickLocation}
        onCancelPickLocation={onCancelPickLocation}
        expanded={expanded}
        onExpandedChange={setExpanded}
        propertiesVisible={propertiesVisible}
        onTogglePropertiesVisible={onTogglePropertiesVisible}
        propertyCount={propertyCount}
        propertyProjectCount={propertyProjectCount}
        propertiesTruncated={propertiesTruncated}
        propertiesLoading={propertiesLoading}
      />

      {/* Folders have their own eye so they and the project areas can be
          shown or hidden independently. The classic map passes no folders
          and shows no pill. */}
      {onToggleFolders && (
        <FolderBoundariesControl
          visible={foldersVisible}
          onToggleVisibility={onToggleFolders}
          folderCount={folderCount}
          boundaryCount={folderBoundaryCount}
          expanded={expanded}
          onExpandedChange={setExpanded}
        />
      )}

      {/* Same spacer the project page uses to separate project data from
          context layers. */}
      <Box sx={{ height: "16px" }} />

      <RealtimeControl
        hotspotsLayer={hotspotsLayer}
        hotspotsFilters={hotspotsFilters}
        onHotspotsFiltersChange={onHotspotsFiltersChange}
        lightningLayer={lightningLayer}
        lightningFilters={lightningFilters}
        onLightningFiltersChange={onLightningFiltersChange}
        hazardsLayer={hazardsLayer}
        hazardsFilters={hazardsFilters}
        onHazardsFiltersChange={onHazardsFiltersChange}
        ignitionPointsLayer={ignitionPointsLayer}
        ignitionPointsFilters={ignitionPointsFilters}
        onIgnitionPointsFiltersChange={onIgnitionPointsFiltersChange}
        burntAreasLayer={burntAreasLayer}
        burntAreasFilters={burntAreasFilters}
        onBurntAreasFiltersChange={onBurntAreasFiltersChange}
        expanded={expanded}
        onExpandedChange={setExpanded}
        simulatingDetection={simulatingDetection}
        onStartSimulateDetection={onStartSimulateDetection}
        onCancelSimulateDetection={onCancelSimulateDetection}
      />

      {measureLayer && (
        <>
          <Box sx={{ height: "16px" }} />
          <MeasureControl layer={measureLayer} expanded={expanded} onExpandedChange={setExpanded} />
        </>
      )}
    </Box>
    </LayerControlSideContext.Provider>
  );
};

export default ProjectListingLayerPanel;
